/**
 * Shared SVG geometry — the single source of truth each platform renderer
 * (`Icon.tsx`, `Icon.native.tsx`) draws from. Pure data: no imports, no
 * platform code.
 *
 * Seed icons are ported from `apps/web/src/assets/` to prove the mechanism;
 * the full icon migration is Phase 3. Keep entries single-color (one fill
 * applied to all paths) — multi-color art belongs in per-app assets until
 * the registry needs it (Rule of Three).
 */
export const icons = {
  caretDown: {
    viewBox: '0 0 16 16',
    paths: [
      'M13.5306 6.53073L8.5306 11.5307C8.46092 11.6007 8.37813 11.6561 8.28696 11.694C8.1958 11.7318 8.09806 11.7513 7.99935 11.7513C7.90064 11.7513 7.8029 11.7318 7.71173 11.694C7.62057 11.6561 7.53778 11.6007 7.4681 11.5307L2.4681 6.53073C2.3272 6.38984 2.24805 6.19874 2.24805 5.99948C2.24805 5.80023 2.3272 5.60913 2.4681 5.46823C2.60899 5.32734 2.80009 5.24818 2.99935 5.24818C3.19861 5.24818 3.3897 5.32734 3.5306 5.46823L7.99997 9.93761L12.4693 5.46761C12.6102 5.32671 12.8013 5.24756 13.0006 5.24756C13.1999 5.24756 13.391 5.32671 13.5318 5.46761C13.6727 5.60851 13.7519 5.7996 13.7519 5.99886C13.7519 6.19812 13.6727 6.38921 13.5318 6.53011L13.5306 6.53073Z',
    ],
  },
  infoCircle: {
    viewBox: '0 0 16 16',
    paths: [
      'M6.75 5.25a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm8 2.75A6.75 6.75 0 1 1 8 1.25 6.758 6.758 0 0 1 14.75 8Zm-1.5 0a5.25 5.25 0 1 0-10.5 0 5.25 5.25 0 0 0 10.5 0Zm-4.5 2.293V8.25A1.25 1.25 0 0 0 7.5 7a.75.75 0 0 0-.25 1.457V10.5a1.25 1.25 0 0 0 1.25 1.25.75.75 0 0 0 .25-1.457Z',
    ],
  },
  pencilSimple: {
    viewBox: '0 0 16 16',
    paths: [
      'M14.3838 4.40893L11.5913 1.61581C11.4752 1.49968 11.3374 1.40756 11.1857 1.34471C11.034 1.28186 10.8714 1.24951 10.7072 1.24951C10.543 1.24951 10.3804 1.28186 10.2287 1.34471C10.077 1.40756 9.93921 1.49968 9.82313 1.61581L2.11626 9.32331C1.99976 9.43904 1.90739 9.57676 1.84452 9.72846C1.78165 9.88017 1.74953 10.0428 1.75001 10.2071V13.0002C1.75001 13.3317 1.8817 13.6496 2.11612 13.8841C2.35054 14.1185 2.66848 14.2502 3.00001 14.2502H5.79313C5.95734 14.2506 6.12001 14.2185 6.27171 14.1556C6.42341 14.0927 6.56112 14.0004 6.67688 13.8839L14.3838 6.17643C14.6181 5.94202 14.7497 5.62414 14.7497 5.29268C14.7497 4.96123 14.6181 4.64334 14.3838 4.40893ZM5.68751 12.7502H3.25001V10.3127L8.50001 5.06268L10.9375 7.50018L5.68751 12.7502ZM12 6.43768L9.56251 4.00018L10.7088 2.85393L13.1463 5.29143L12 6.43768Z',
    ],
  },
  // Ported from @phosphor-icons/react 2.0.10 "Package" (bold) — the glyph CWA's
  // OrderInfoHeader rendered via the phosphor runtime dependency. MIT licensed.
  package: {
    viewBox: '0 0 256 256',
    paths: [
      'M225.6,62.64l-88-48.17a19.91,19.91,0,0,0-19.2,0l-88,48.17A20,20,0,0,0,20,80.19v95.62a20,20,0,0,0,10.4,17.55l88,48.17a19.89,19.89,0,0,0,19.2,0l88-48.17A20,20,0,0,0,236,175.81V80.19A20,20,0,0,0,225.6,62.64ZM128,36.57,200,76,178.57,87.73l-72-39.42Zm0,78.83L56,76,81.56,62l72,39.41ZM44,96.79l72,39.4v76.67L44,173.44Zm96,116.07V136.19l24-13.13V152a12,12,0,0,0,24,0V109.92l24-13.13v76.65Z',
    ],
  },
  // Ported from @phosphor-icons/react 2.0.10 "Warning" (bold) — the glyph CWA's
  // delay Alert rendered; CMA's hand-drawn WarningSVG is superseded by this one.
  warning: {
    viewBox: '0 0 256 256',
    paths: [
      'M240.26,186.1,152.81,34.23h0a28.74,28.74,0,0,0-49.62,0L15.74,186.1a27.45,27.45,0,0,0,0,27.71A28.31,28.31,0,0,0,40.55,228h174.9a28.31,28.31,0,0,0,24.79-14.19A27.45,27.45,0,0,0,240.26,186.1Zm-20.8,15.7a4.46,4.46,0,0,1-4,2.2H40.55a4.46,4.46,0,0,1-4-2.2,3.56,3.56,0,0,1,0-3.73L124,46.2a4.77,4.77,0,0,1,8,0l87.44,151.87A3.56,3.56,0,0,1,219.46,201.8ZM116,136V104a12,12,0,0,1,24,0v32a12,12,0,0,1-24,0Zm28,40a16,16,0,1,1-16-16A16,16,0,0,1,144,176Z',
    ],
  },
} as const
